import './styles.scss';

import { AudioOutlined, BorderOutlined, DeleteOutlined } from '@ant-design/icons';
import { Button } from '@components/Button/Button';
import { Alert, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';

// Порядок предпочтения форматов: webm/opus поддерживают Chrome, Edge и Firefox,
// Safari пишет только audio/mp4
const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/mp4',
];

const DEFAULT_MIME_TYPE = 'audio/webm';

const TIMER_INTERVAL_MS = 500;

/**
 * Запись доступна, если браузер умеет MediaRecorder и getUserMedia.
 * getUserMedia есть только в защищённом контексте (HTTPS или localhost).
 */
export const isAudioRecordingSupported = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.MediaRecorder !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia;

const pickMimeType = (): string | undefined =>
  PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported?.(type));

export const getAudioFileExtension = (mimeType?: string): string => {
  if (mimeType?.includes('ogg')) return 'ogg';
  if (mimeType?.includes('mp4')) return 'm4a';
  return 'webm';
};

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
};

const getErrorMessage = (error: unknown): string => {
  const name = (error as DOMException)?.name;

  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Нет доступа к микрофону. Разрешите доступ в настройках браузера и повторите.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'Микрофон не найден. Подключите микрофон и повторите.';
  }
  if (name === 'NotReadableError') {
    return 'Микрофон занят другим приложением.';
  }
  return 'Не удалось начать запись.';
};

export interface IAudioRecorderProps {
  value?: Blob | null;
  onChange?: (value?: Blob) => void;
  disabled?: boolean;
}

/**
 * Запись звука с микрофона через MediaRecorder: старт/стоп, таймер, прослушивание.
 * Значение — Blob записи (webm/opus, в Safari — mp4); подходит как поле формы (value/onChange).
 */
export const AudioRecorder = ({ value, onChange, disabled }: IAudioRecorderProps) => {
  const [isSupported] = useState(isAudioRecordingSupported);
  const [isRecording, setIsRecording] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const isMountedRef = useRef(true);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }

    const url = URL.createObjectURL(value);
    setPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [value]);

  const stopTimer = () => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  // При закрытии формы во время записи освобождаем микрофон и не отдаём результат
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      stopTimer();
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop();
      }
      releaseStream();
    };
  }, []);

  const start = async () => {
    setError(null);
    setIsStarting(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      if (!isMountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      streamRef.current = stream;
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => {
        stopTimer();
        releaseStream();
        recorderRef.current = null;

        if (!isMountedRef.current) return;

        setIsRecording(false);
        const blob = new Blob(chunksRef.current, {
          type: recorder.mimeType || mimeType || DEFAULT_MIME_TYPE,
        });
        chunksRef.current = [];

        if (blob.size > 0) {
          onChange?.(blob);
        } else {
          setError('Запись пустая. Проверьте микрофон и повторите.');
        }
      };

      const startedAt = Date.now();
      setElapsed(0);
      timerRef.current = window.setInterval(
        () => setElapsed(Math.floor((Date.now() - startedAt) / 1000)),
        TIMER_INTERVAL_MS,
      );

      // Данные забираются порциями раз в секунду, чтобы длинная запись не терялась целиком
      recorder.start(1000);
      setIsRecording(true);
      onChange?.(undefined);
    } catch (e) {
      releaseStream();
      setError(getErrorMessage(e));
    } finally {
      if (isMountedRef.current) setIsStarting(false);
    }
  };

  const stop = () => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
    }
  };

  const reset = () => {
    setElapsed(0);
    setError(null);
    onChange?.(undefined);
  };

  if (!isSupported) {
    return (
      <Alert
        type="info"
        showIcon
        message="Запись с микрофона недоступна в этом браузере или без HTTPS. Загрузите файл записи."
      />
    );
  }

  return (
    <div className="audio-recorder">
      <div className="audio-recorder-controls">
        {isRecording ? (
          <Button
            danger
            type="primary"
            icon={<BorderOutlined />}
            text="Остановить"
            action={stop}
          />
        ) : (
          <Button
            type="primary"
            icon={<AudioOutlined />}
            text={value ? 'Записать заново' : 'Начать запись'}
            action={start}
            loading={isStarting}
            disabled={disabled}
          />
        )}
        {(isRecording || value) && (
          <Typography.Text
            className={`audio-recorder-timer ${isRecording ? 'recording' : ''}`}>
            {isRecording && <span className="audio-recorder-dot" />}
            {formatDuration(elapsed)}
          </Typography.Text>
        )}
        {value && !isRecording && (
          <Button
            type="text"
            icon={<DeleteOutlined />}
            text="Удалить"
            action={reset}
            disabled={disabled}
          />
        )}
      </div>
      {previewUrl && !isRecording && (
        // Расшифровки у предпрослушивания нет, дорожка субтитров пустая
        <audio className="audio-recorder-preview" controls src={previewUrl}>
          <track kind="captions" />
        </audio>
      )}
      {error && <Alert type="error" showIcon message={error} />}
    </div>
  );
};
